import * as React from "react"
import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { domainLabel, type Signal } from "@/lib/manufacturerSignals"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  manufacturerId: number
  domains: string[]
  signal?: Signal | null
}

type FormData = {
  domain: string
  title: string
  body: string
  source_url: string
  observed_on: string
}

// Lean manual-entry / edit dialog for a manufacturer signal — only the fields the
// schema carries. A new entry lands approved (origin "manual") server-side.
export function ManufacturerSignalFormDialog({
  open,
  onOpenChange,
  manufacturerId,
  domains,
  signal,
}: Props) {
  const isEdit = Boolean(signal)

  const initial = React.useMemo<FormData>(
    () => ({
      domain: signal?.domain ?? "",
      title: signal?.title ?? "",
      body: signal?.body ?? "",
      source_url: signal?.source_url ?? "",
      observed_on: signal?.observed_on ?? "",
    }),
    [signal],
  )

  const { data, setData, post, patch, processing, errors, reset, clearErrors } =
    useForm<FormData>(initial)

  // Re-seed the form whenever a different signal (or create vs edit) is opened.
  React.useEffect(() => {
    if (open) {
      setData(initial)
      clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, signal?.id])

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const onSuccess = () => {
      reset()
      onOpenChange(false)
    }
    if (isEdit && signal) {
      patch(`/admin/manufacturers/${manufacturerId}/signals/${signal.id}`, {
        onSuccess,
        preserveScroll: true,
      })
    } else {
      post(`/admin/manufacturers/${manufacturerId}/signals`, { onSuccess, preserveScroll: true })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit sinyal" : "Tambah sinyal manual"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit}>
          <div className="grid gap-4 px-0.5 py-1 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="title">Judul</label>
              <Input
                id="title"
                value={data.title}
                onChange={(e) => setData("title", e.target.value)}
                className="mt-1"
              />
              {errors.title && <p className="mt-1 text-sm text-danger">{errors.title}</p>}
            </div>

            <div>
              <label htmlFor="domain">Domain</label>
              <Select
                id="domain"
                className="mt-1"
                value={data.domain}
                onChange={(e) => setData("domain", e.target.value)}
              >
                <option value="">— Tidak ada —</option>
                {domains.map((d) => (
                  <option key={d} value={d}>
                    {domainLabel(d)}
                  </option>
                ))}
              </Select>
              {errors.domain && <p className="mt-1 text-sm text-danger">{errors.domain}</p>}
            </div>

            <div>
              <label htmlFor="observed_on">Tanggal kejadian</label>
              <Input
                id="observed_on"
                type="date"
                value={data.observed_on}
                onChange={(e) => setData("observed_on", e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="body">Deskripsi</label>
              <textarea
                id="body"
                value={data.body}
                onChange={(e) => setData("body", e.target.value)}
                rows={3}
                className="form-control mt-1"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="source_url">URL sumber</label>
              <Input
                id="source_url"
                value={data.source_url}
                onChange={(e) => setData("source_url", e.target.value)}
                placeholder="https://…"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Batal
            </Button>
            <Button type="submit" disabled={processing}>
              {isEdit ? "Simpan" : "Tambah sinyal"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
